import toast, { Toaster } from "react-hot-toast";

const VARIANTS = {
    inactive: {
        accent: "linear-gradient(145deg, #e85d4c 0%, #c44536 100%)",
        glow: "rgba(196, 69, 54, 0.32)",
        border: "rgba(196, 69, 54, 0.2)",
        bar: "#c44536",
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                    d="M7 11V8a5 5 0 0 1 10 0v3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                />
                <rect x="5" y="11" width="14" height="10" rx="2.5" stroke="currentColor" strokeWidth="2" />
                <circle cx="12" cy="16" r="1.4" fill="currentColor" />
            </svg>
        ),
    },
    error: {
        accent: "linear-gradient(145deg, #ff6b5a 0%, #d94836 100%)",
        glow: "rgba(217, 72, 54, 0.28)",
        border: "rgba(217, 72, 54, 0.18)",
        bar: "#d94836",
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                <path d="M12 8v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="16.5" r="1.2" fill="currentColor" />
            </svg>
        ),
    },
    warning: {
        accent: "linear-gradient(145deg, #f0a202 0%, #d97706 100%)",
        glow: "rgba(217, 119, 6, 0.28)",
        border: "rgba(217, 119, 6, 0.2)",
        bar: "#d97706",
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                    d="M12 4.5 21 20H3L12 4.5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                />
                <path d="M12 10v4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="17.2" r="1.1" fill="currentColor" />
            </svg>
        ),
    },
};

export function showLoginToast({ title, message, variant = "error" }) {
    const theme = VARIANTS[variant] || VARIANTS.error;

    toast.custom(
        (t) => (
            <div
                role="alert"
                style={{
                    display: "flex",
                    alignItems: "stretch",
                    gap: 0,
                    width: "min(420px, calc(100vw - 32px))",
                    borderRadius: 16,
                    overflow: "hidden",
                    background:
                        "linear-gradient(145deg, #ffffff 0%, #f8fafc 48%, #f1f5f9 100%)",
                    border: `1px solid ${theme.border}`,
                    boxShadow: `0 20px 45px rgba(15, 40, 70, 0.16), 0 4px 12px ${theme.glow}`,
                    opacity: t.visible ? 1 : 0,
                    transform: t.visible ? "translateY(0) scale(1)" : "translateY(-10px) scale(0.98)",
                    transition: "opacity 0.28s ease, transform 0.28s ease",
                }}
            >
                <div
                    style={{
                        width: 5,
                        flexShrink: 0,
                        background: theme.bar,
                    }}
                />
                <div
                    style={{
                        display: "flex",
                        gap: 14,
                        alignItems: "flex-start",
                        padding: "15px 14px 15px 16px",
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    <div
                        style={{
                            width: 42,
                            height: 42,
                            borderRadius: 12,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            background: theme.accent,
                            boxShadow: `0 10px 18px ${theme.glow}`,
                        }}
                    >
                        {theme.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
                        <div
                            style={{
                                fontWeight: 700,
                                fontSize: 15,
                                letterSpacing: "-0.01em",
                                color: "#152238",
                                marginBottom: message ? 4 : 0,
                                lineHeight: 1.3,
                            }}
                        >
                            {title}
                        </div>
                        {message ? (
                            <div
                                style={{
                                    fontSize: 13,
                                    lineHeight: 1.5,
                                    color: "#5b6b7f",
                                }}
                            >
                                {message}
                            </div>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        aria-label="Dismiss"
                        onClick={() => toast.dismiss(t.id)}
                        style={{
                            border: "none",
                            background: "transparent",
                            color: "#8a97a8",
                            cursor: "pointer",
                            fontSize: 20,
                            lineHeight: 1,
                            padding: "0 2px",
                            marginTop: -2,
                            flexShrink: 0,
                        }}
                    >
                        ×
                    </button>
                </div>
            </div>
        ),
        { duration: 5500, position: "top-center" }
    );
}

export function LoginToaster() {
    return (
        <Toaster
            position="top-center"
            gutter={14}
            containerStyle={{ top: 18 }}
            toastOptions={{
                duration: 4500,
            }}
        />
    );
}

export function showLoginErrorByStatus(status) {
    if (status === 401) {
        showLoginToast({
            title: "Invalid credentials",
            message: "Please check your email and password, then try again.",
            variant: "error",
        });
        return;
    }
    if (status === 403) {
        showLoginToast({
            title: "Account currently inactive",
            message: "Please contact your administrator or support team for assistance.",
            variant: "inactive",
        });
        return;
    }
    if (status === 404) {
        showLoginToast({
            title: "Account not approved yet",
            message: "Your login is pending approval. Please check back later.",
            variant: "warning",
        });
        return;
    }
    if (status === 405) {
        showLoginToast({
            title: "No account found",
            message: "We couldn’t find a user account with these details.",
            variant: "error",
        });
        return;
    }
    if (status === 400) {
        showLoginToast({
            title: "Missing details",
            message: "Please fill in all required fields.",
            variant: "warning",
        });
    }
}
